import { action, computed, observable, reaction, IReactionDisposer, runInAction } from 'mobx';
import { IVideoProps, TBgColor } from 'telia-front-react';
import ErrorUtil from '../error/ErrorUtil';
import API from '../util/ApiUtil';
import { FetchStatus } from '../util/FetchStatus';
import { getCustomerPathSegment } from '../util/SegmentUtil';
import LanguageStore, { Language } from './LanguageStore';
import { RootStore } from './RootStore';
import SegmentStore, { SEGMENT_ARI, SEGMENT_ERA } from './SegmentStore';
import SsoStore, { AuthCustomer, AuthDealer } from './SsoStore';

export interface ProductRule {
  rule: string;
  value: boolean;
}

export interface BannerData {
  content: string;
  title: string;
  desktopImage: string;
  mobileImage: string;
  clientTypes?: string[];
  productRules?: ProductRule[];
  priority?: 0 | 1 | 2 | 3;
  backgroundColor?: TBgColor | string;
  video?: IVideoProps;
  themeVariant?: 'dark' | 'light';
}

export interface BannerProducts {
  [key: string]: boolean;
}

class BannersStore {
  @observable accessor status: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor allBanners = new Map<string, BannerData[]>();
  @observable accessor bannerProducts = {};
  @observable accessor previousCustomerId: number = null;
  @observable accessor bannerRules: string[] = [];
  private customerIdReaction: IReactionDisposer = null;

  private languageStore: LanguageStore;
  private ssoStore: SsoStore;
  private segmentStore: SegmentStore;

  constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;
    this.ssoStore = rootStore.ssoStore;
    this.segmentStore = rootStore.segmentStore;

    if (rootStore.initialState && rootStore.initialState.banners && !this.languageStore.initialLanguageChanged) {
      this.allBanners.set(
        this.getBannerMapsKey(this.languageStore.currentLanguage, getCustomerPathSegment(this.segmentStore.currentSegment)),
        rootStore.initialState.banners as BannerData[],
      );
      this.bannerRules = this.calculateBannerRules();
      this.status = FetchStatus.FETCHED;
    }
  }

  public fetchBanners = async () => {
    if (!this.shouldFetchBanner()) {
      return Promise.resolve();
    }
    runInAction(() => {
      this.status = FetchStatus.FETCHING;
    });
    try {
      const {
        languageStore: { currentLanguage },
      } = this;
      const customerPathSegment = getCustomerPathSegment(this.segmentStore.currentSegment);
      const bannersKey = this.getBannerMapsKey(currentLanguage, customerPathSegment);
      const response = await API.get<BannerData[]>(`/banners/${currentLanguage}/${customerPathSegment}`);
      runInAction(() => {
        this.setBanners(bannersKey, response.data);
        this.status = FetchStatus.FETCHED;
      });
      this.fetchBannerProducts(this.calculateBannerRules());
    } catch (e) {
      runInAction(() => {
        this.status = FetchStatus.ERROR;
      });
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
    }
  };

  public fetchBannerProducts = async (bannerRules: string[]) => {
    try {
      if (!this.ssoStore.customerId || !bannerRules.length) {
        this.setBannerProducts({});
        return Promise.resolve();
      } else if (this.previousCustomerId === this.ssoStore.customerId && bannerRules.every((rule: string) => this.bannerRules.includes(rule))) {
        return Promise.resolve();
      }
      runInAction(() => {
        this.previousCustomerId = this.ssoStore.customerId;
        this.bannerRules = bannerRules;
      });
      const response = await API.get<BannerProducts>('/product/has-banner-products', { params: { bannerRules } });
      this.setBannerProducts(response.data);
    } catch (e) {
      this.setBannerProducts({});
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
    }
  };

  @action
  private setBanners(key: string, banners: BannerData[]) {
    this.allBanners.set(key, banners);
  }

  private calculateBannerRules = (): string[] => {
    const rulesToFetch = new Set<string>();
    this.allBanners.forEach((banners: BannerData[]) =>
      banners?.forEach((banner: BannerData) => banner?.productRules?.forEach((rule: ProductRule) => rulesToFetch.add(rule.rule))),
    );
    return Array.from(rulesToFetch).sort();
  };

  @action
  private setBannerProducts(bannerProducts: BannerProducts) {
    this.bannerProducts = bannerProducts;
  }

  public subscribeToCustomerChangeReaction() {
    this.customerIdReaction = reaction(
      () => this.ssoStore.customerId,
      () => this.fetchBannerProducts(this.calculateBannerRules()),
      { fireImmediately: true },
    );
  }

  public unsubscribeToCustomerChangeReaction() {
    if (this.customerIdReaction) {
      this.customerIdReaction();
    }
  }

  @computed
  public get bannersToDisplay() {
    const customerPathSegment = getCustomerPathSegment(this.segmentStore.currentSegment);
    const key = this.getBannerMapsKey(this.languageStore.currentLanguage, customerPathSegment);
    const banners = this.allBanners
      .get(key)
      ?.filter((banner: BannerData) =>
        BannersStore.filterBannersByClientType(banner.clientTypes, this.ssoStore.customer, this.ssoStore.dealer, customerPathSegment),
      )
      .filter((banner: BannerData) => BannersStore.filterBannersByProducts(banner.productRules, this.bannerProducts))
      .sort(BannersStore.bannerSort);
    return banners || [];
  }

  public static bannerSort = (a: BannerData, b: BannerData): number => {
    return a.priority < b.priority ? 1 : -1;
  };

  public static singleCloudinaryUrlBySize = (size: string, url: string): string => {
    if (!url) {
      return '';
    }
    if (url.includes('https://res.cloudinary.com/telia/image/upload/')) {
      url = url.replace('https://res.cloudinary.com/telia/image/upload/', '');
    }
    if (url.startsWith('/upload')) {
      url = url.substr('/upload'.length + 1);
    }
    return `https://res.cloudinary.com/telia/image/upload/f_auto,fl_lossy,q_auto:best,w_${size}/${url} ${size}w`;
  };

  public getDesktopSrcSetForBanner(index: number) {
    const bannerUrl = this.bannersToDisplay[index].desktopImage;
    return ['768', '1280', '2560', '5120'].map((size) => BannersStore.singleCloudinaryUrlBySize(size, bannerUrl)).join(',');
  }

  public getMobileSrcSetForBanner(index: number) {
    const bannerUrl = this.bannersToDisplay[index].mobileImage;
    return ['320', '640', '960', '1280'].map((size) => BannersStore.singleCloudinaryUrlBySize(size, bannerUrl)).join(',');
  }

  public static filterBannersByClientType(bannerClientTypes: string[], customer: AuthCustomer, dealer: AuthDealer, segment: string) {
    if (!bannerClientTypes || !bannerClientTypes.length || bannerClientTypes[0] === 'all') {
      return true;
    }
    if (bannerClientTypes.includes('business_auth') && customer && customer.type === 'B2B') {
      return true;
    }
    if (bannerClientTypes.includes('private_auth') && customer && customer.type === 'B2C') {
      return true;
    }
    if (bannerClientTypes.includes('business_anonymous') && !customer && segment === SEGMENT_ARI) {
      return true;
    }
    if (bannerClientTypes.includes('private_anonymous') && !customer && segment === SEGMENT_ERA) {
      return true;
    }
    if (bannerClientTypes.includes('employee') && !customer && !!dealer) {
      return true;
    }
    return bannerClientTypes.includes('anonymous') && !customer;
  }

  public static filterBannersByProducts(productRules: ProductRule[], bannerProducts: BannerProducts) {
    return !productRules?.slice()?.length || productRules.every((rule: ProductRule) => bannerProducts[rule.rule] === rule.value);
  }

  private shouldFetchBanner() {
    return (
      !this.allBanners ||
      !this.allBanners.get(this.getBannerMapsKey(this.languageStore.currentLanguage, getCustomerPathSegment(this.segmentStore.currentSegment)))
    );
  }

  private getBannerMapsKey(language: Language, segment: string) {
    return `${language}_${segment}`;
  }
}

export default BannersStore;
